import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LanguageContext = createContext(undefined);
const STORAGE_KEY = "bv.language";

const translations = {
  en: {
    "Welcome back": "Welcome back",
    Logout: "Logout",
    Dashboard: "Dashboard",
    Assessments: "Assessments",
    Documents: "Documents",
    Admin: "Admin",
    "Support Ticket": "Support Ticket",
    Settings: "Settings",
    "Aim higher with the help of AI": "Aim higher with the help of AI",
    "Change language": "Change language",
    "Select language": "Select language",
    English: "English",
    French: "French",
    Cancel: "Cancel",
    Save: "Save",
    Add: "Add",
    Edit: "Edit",
    Upload: "Upload",
    Delete: "Delete",
    Download: "Download",
    Submit: "Submit",
    Back: "Back",
    "Back to Assessments": "Back to Assessments",
    Interviewees: "Interviewees",
    Name: "Name",
    Role: "Role",
    Type: "Type",
    Interview: "Interview",
    "Files / Transcript": "Files / Transcript",
    Actions: "Actions",
    "Not uploaded": "Not uploaded",
    "No transcript available.": "No transcript available.",
    "Choose recording mode": "Choose recording mode",
    "Mic only": "Mic only",
    "Meeting audio + Mic": "Meeting audio + Mic",
    Transcript: "Transcript",
    "AI Policies & Guidelines": "AI Policies & Guidelines",
    "AI Readiness Assessment": "AI Readiness Assessment",
    "AI Workflow Assessment": "AI Workflow Assessment",
    "Download interview sheets and reference materials":
      "Download interview sheets and reference materials",
    "Not authorized. Please log in.": "Not authorized. Please log in.",
    "Failed to fetch assessments.": "Failed to fetch assessments.",
    "Failed to create assessment.": "Failed to create assessment.",
    "common.cancel": "Cancel",
    "common.select": "Select",
    "form.companyName": "Company Name",
    "form.address1": "Address line 1",
    "form.address2": "Address line 2",
    "form.city": "City",
    "form.stateProv": "State / Province",
    "form.country": "Country",
    "form.vertical": "Vertical",
    "form.keyContact": "Key Contact",
    "form.position": "Position",
    "form.contactEmail": "Contact Email",
    "form.contactPhone": "Contact Phone",
    "Create Assessment": "Create Assessment",
    "Active Assessments": "Active Assessments",
    "Needs Attention": "Needs Attention",
    "No assessments found.": "No assessments found.",
    "assessments.manageAllCompanyAssessments":
      "Manage all company assessments",
    "assessments.deleteAssessment": "Delete Assessment",
    "assessments.deleteConfirmPrefix":
      "Are you sure you want to delete the assessment for",
    "assessments.deleteConfirmSuffix": "? This action cannot be undone.",
    "assessments.thisCompany": "this company",
    "assessments.selectNewPlanToContinue": "Select a new plan to continue:",
    "assessments.allRightsReserved": "All Rights Reserved",
    "assessments.companyNameRequired": "Company Name is required.",
    "assessments.missingAccountInformation": "Missing account information.",
    "assessments.unableToValidateSubscription":
      "Unable to validate subscription.",
    "assessments.unableToStartCheckout":
      "Unable to start checkout. Please try again later.",
    "assessments.failedToCreateCheckoutSession":
      "Failed to create checkout session.",
    "assessments.failedToSaveAssessment": "Failed to save assessment.",
    "assessments.notAuthorized": "Not authorized.",
    "assessments.missingAssessmentId": "Missing assessment id.",
    "assessments.failedToDeleteAssessment": "Failed to delete assessment.",
    "status.active": "Active",
    "status.completed": "Completed",
    "status.aiWorkflowCompleted": "AI Workflow Completed",
    "status.aiPoliciesCompleted": "AI Policies Completed",
    Untitled: "Untitled",
    "dashboard.activeAssessments": "Active Assessments",
    "dashboard.completed": "Completed Assessments",
    "dashboard.total": "Total Assessments",
    "dashboard.currentlyInProgress": "Currently in progress",
    "dashboard.allTime": "All time",
    "dashboard.allAssessments": "All assessments",
    "dashboard.checking": "Checking...",
    "dashboard.newAssessment": "New Assessment",
    "dashboard.createNewAssessment": "Create New Assessment",
    "dashboard.subscriptionRequired": "Subscription Required",
    "dashboard.subscriptionInfo":
      "You need an active subscription before you can create a new assessment.",
    "dashboard.selectPlan": "Select a plan to continue:",
    "dashboard.startingCheckout": "Starting checkout...",
    "dashboard.select": "Select",
    "dashboard.complete": "complete",
    "dashboard.interviewee": "Interviewee",
    "dashboard.missingFiles": "missing files",
    "dashboard.aimHigher": "Aim higher with the help of AI",
    "dashboard.title": "Dashboard",
    "dashboard.activeAssessmentsLabel": "Active Assessments",
    "dashboard.completedAssessmentsLabel": "Completed Assessments",
    "dashboard.needsAttention": "Needs Attention",
    "dashboard.viewAll": "View all",
    "dashboard.noAssessmentsFound": "No assessments found.",
    "Processing…": "Processing…",
    "Processing… This may take a few minutes":
      "Processing… This may take a few minutes",
    "Overall Progress": "Overall Progress",
    "completed with transcripts": "completed with transcripts",
    "Document ready for download": "Document ready for download",
    "All interviews complete": "All interviews complete",
    "Complete all interviews & upload transcripts first":
      "Complete all interviews & upload transcripts first",
    "Ready to generate": "Ready to generate",
    "At least 1 interview with transcript required":
      "At least 1 interview with transcript required",
    "interviewee(s)": "interviewee(s)",
    "Delete Document": "Delete Document",
    "assessment.deleteDocumentConfirmPrefix":
      "Are you sure you want to delete the",
    "assessment.deleteDocumentConfirmSuffix":
      "document? This action cannot be undone.",
    "Edit Interviewee": "Edit Interviewee",
    Yes: "Yes",
    No: "No",
    Notice: "Notice",
    Notification: "Notification",
    "Action failed": "Action failed",
    "Unable to complete the action.": "Unable to complete the action.",
    "Record only your microphone, or include meeting/system audio by sharing a tab/window with system audio enabled.":
      "Record only your microphone, or include meeting/system audio by sharing a tab/window with system audio enabled.",
    "Are you sure you want to proceed? This action cannot be undone.":
      "Are you sure you want to proceed? This action cannot be undone.",
    "Generating document… please wait.": "Generating document… please wait.",
    "Elapsed": "Elapsed",
    "Add Interviewees": "Add Interviewees",
    "Add Interviewee": "Add Interviewee",
    "Missing fields": "Missing fields",
    "Name and Position are required.": "Name and Position are required.",
    "Invalid file": "Invalid file",
    "Please upload a CSV file.": "Please upload a CSV file.",
    "Upload CSV": "Upload CSV",
    "CSV format: Name, Role, Type, Email, Phone (one per row). First row can be a header.":
      "CSV format: Name, Role, Type, Email, Phone (one per row). First row can be a header.",
    "Choose CSV file": "Choose CSV file",
    "interviewee(s) found": "interviewee(s) found",
    "Submit Support Ticket": "Submit Support Ticket",
    Company: "Company",
    Plan: "Plan",
    "Contact Name": "Contact Name",
    Email: "Email",
    Phone: "Phone",
    "Category *": "Category *",
    "Priority *": "Priority *",
    "Subject *": "Subject *",
    Description: "Description",
    "Select category": "Select category",
    "Select priority": "Select priority",
    "Technical Issue": "Technical Issue",
    "Billing / Account": "Billing / Account",
    "Feature Request": "Feature Request",
    "Assessment Help": "Assessment Help",
    Other: "Other",
    Low: "Low",
    Medium: "Medium",
    High: "High",
    Urgent: "Urgent",
    "Brief summary of your issue": "Brief summary of your issue",
    "Provide details about your issue, including steps to reproduce if applicable...":
      "Provide details about your issue, including steps to reproduce if applicable...",
    "Submit Ticket": "Submit Ticket",
    "Please fill in all required fields": "Please fill in all required fields",
    "Failed to submit ticket.": "Failed to submit ticket.",
    "Support ticket submitted": "Support ticket submitted",
    "support.ticketSubmittedPrefix": "Ticket for",
    "support.ticketSubmittedSuffix": "has been sent. Request submitted.",
    "Submit failed": "Submit failed",
    "Could not submit support ticket.": "Could not submit support ticket.",
    "settings.subtitle":
      "Configure branding, language, subscription, and billing preferences.",
    "Account Settings": "Account Settings",
    "Update your contact information and password.":
      "Update your contact information and password.",
    "Full Name": "Full Name",
    "Enter your full name": "Enter your full name",
    "Enter your phone number": "Enter your phone number",
    "New Password": "New Password",
    "Leave blank to keep current": "Leave blank to keep current",
    "Confirm Password": "Confirm Password",
    "Confirm new password": "Confirm new password",
    "White Label": "White Label",
    "Your company name and logo appear on all generated documents.":
      "Your company name and logo appear on all generated documents.",
    "Enter your company name": "Enter your company name",
    "Company Logo": "Company Logo",
    "Click to upload logo": "Click to upload logo",
    "PNG, JPG, SVG up to 2MB": "PNG, JPG, SVG up to 2MB",
    "Document Header Preview": "Document Header Preview",
    "Your Company Name": "Your Company Name",
    Language: "Language",
    "Select the language for generated output documents and reports.":
      "Select the language for generated output documents and reports.",
    "Output Language": "Output Language",
    "This determines the language used in AI Readiness Assessments and Policy documents.":
      "This determines the language used in AI Readiness Assessments and Policy documents.",
    Subscription: "Subscription",
    "Billed quarterly at": "Billed quarterly at",
    "Pause Subscription": "Pause Subscription",
    "Quarterly Invoices": "Quarterly Invoices",
    "Loading upcoming invoices...": "Loading upcoming invoices...",
    Paid: "Paid",
    Upcoming: "Upcoming",
    "No upcoming invoices found.": "No upcoming invoices found.",
    "Upgrade to Pro": "Upgrade to Pro",
    "settings.upgradeCopy":
      "You've been active for 6+ months and are approaching your assessment limit. Upgrade to Pro for 3 assessments per month at $500/assessment.",
    "months remaining": "months remaining",
    "Upgrade to": "Upgrade to",
    "subscription.enterpriseUpgradeCopy":
      "You're exceeding 3 assessments per month. Upgrade to Enterprise for unlimited assessments and priority support.",
    Billing: "Billing",
    "Manage your payment method and billing details.":
      "Manage your payment method and billing details.",
    "Billing Details": "Billing Details",
    "Next billing": "Next billing",
    Amount: "Amount",
    "Open Billing Portal": "Open Billing Portal",
    "Saving...": "Saving...",
    "Save Settings": "Save Settings",
    "Account Settings Updated": "Account Settings Updated",
    "Your account details have been saved successfully.":
      "Your account details have been saved successfully.",
    Done: "Done",
    "Please contact west@gowest.ai to pause your account and payments.":
      "Please contact west@gowest.ai to pause your account and payments.",
    "Invoice load failed": "Invoice load failed",
    "Unable to load invoices.": "Unable to load invoices.",
    "Could not load upcoming invoices.": "Could not load upcoming invoices.",
    "Cloudinary widget failed to load.": "Cloudinary widget failed to load.",
    "Missing user id. Please sign in again.":
      "Missing user id. Please sign in again.",
    "Upload failed": "Upload failed",
    "Could not upload logo.": "Could not upload logo.",
    "Logo uploaded": "Logo uploaded",
    "Your company logo has been saved.": "Your company logo has been saved.",
    "Unable to open upload widget.": "Unable to open upload widget.",
    "Please upload an image file.": "Please upload an image file.",
    "Settings saved": "Settings saved",
    "Your preferences have been updated.": "Your preferences have been updated.",
    "Full name is required.": "Full name is required.",
    "Full name required": "Full name required",
    "Enter your full name before saving account settings.":
      "Enter your full name before saving account settings.",
    "Phone number is required.": "Phone number is required.",
    "Phone number required": "Phone number required",
    "Enter your phone number before saving account settings.":
      "Enter your phone number before saving account settings.",
    "Email is required.": "Email is required.",
    "Email missing": "Email missing",
    "Please sign in again before updating account settings.":
      "Please sign in again before updating account settings.",
    "New password is required.": "New password is required.",
    "New password required": "New password required",
    "Enter a new password before confirming it.":
      "Enter a new password before confirming it.",
    "Passwords do not match.": "Passwords do not match.",
    "Passwords do not match": "Passwords do not match",
    "Confirm your new password before saving.":
      "Confirm your new password before saving.",
    Saved: "Saved",
    "Account settings updated.": "Account settings updated.",
    "Save failed": "Save failed",
    "Failed to save account settings.": "Failed to save account settings.",
    "Assessment limit unavailable": "Assessment limit unavailable",
    "Quarterly invoice": "Quarterly invoice",
    "1 assessment/month (3/quarter)": "1 assessment/month (3/quarter)",
    "3 assessments/month": "3 assessments/month",
    "Unlimited assessments": "Unlimited assessments",
    Custom: "Custom",
    Close: "Close",
    Confirm: "Confirm",
    "Are you sure?": "Are you sure?",
  },
  fr: {
    "Welcome back": "Bon retour",
    Logout: "Se déconnecter",
    Dashboard: "Tableau de bord",
    Assessments: "Évaluations",
    Documents: "Documents",
    Admin: "Administrateur",
    "Support Ticket": "Ticket d’assistance",
    Settings: "Paramètres",
    "Aim higher with the help of AI": "Visez plus haut avec l’aide de l’IA",
    "Change language": "Changer de langue",
    "Select language": "Sélectionner la langue",
    English: "Anglais",
    French: "Français",
    Cancel: "Annuler",
    Save: "Enregistrer",
    Add: "Ajouter",
    Edit: "Modifier",
    Upload: "Téléverser",
    Delete: "Supprimer",
    Download: "Télécharger",
    Submit: "Soumettre",
    Back: "Retour",
    "Back to Assessments": "Retour aux évaluations",
    Interviewees: "Personnes interviewées",
    Name: "Nom",
    Role: "Rôle",
    Type: "Type",
    Interview: "Entretien",
    "Files / Transcript": "Fichiers / Transcription",
    Actions: "Actions",
    "Not uploaded": "Non téléversé",
    "No transcript available.": "Aucune transcription disponible.",
    "Choose recording mode": "Choisir le mode d’enregistrement",
    "Mic only": "Micro uniquement",
    "Meeting audio + Mic": "Audio de réunion + micro",
    Transcript: "Transcription",
    "AI Policies & Guidelines": "Politiques et lignes directrices IA",
    "AI Readiness Assessment": "Évaluation de la préparation IA",
    "AI Workflow Assessment": "Évaluation du flux de travail IA",
    "Download interview sheets and reference materials":
      "Télécharger les fiches d’entretien et les documents de référence",
    "Not authorized. Please log in.": "Non autorisé. Veuillez vous connecter.",
    "Failed to fetch assessments.": "Échec du chargement des évaluations.",
    "Failed to create assessment.": "Échec de la création de l’évaluation.",
    "common.cancel": "Annuler",
    "common.select": "Sélectionner",
    "form.companyName": "Nom de l'entreprise",
    "form.address1": "Adresse ligne 1",
    "form.address2": "Adresse ligne 2",
    "form.city": "Ville",
    "form.stateProv": "État / Province",
    "form.country": "Pays",
    "form.vertical": "Secteur d'activité",
    "form.keyContact": "Personne-ressource clé",
    "form.position": "Poste",
    "form.contactEmail": "E-mail de contact",
    "form.contactPhone": "Téléphone de contact",
    "Create Assessment": "Créer une évaluation",
    "Active Assessments": "Évaluations actives",
    "Needs Attention": "Nécessite une attention",
    "No assessments found.": "Aucune évaluation trouvée.",
    "assessments.manageAllCompanyAssessments":
      "Gérer toutes les évaluations de l’entreprise",
    "assessments.deleteAssessment": "Supprimer l’évaluation",
    "assessments.deleteConfirmPrefix":
      "Êtes-vous sûr de vouloir supprimer l’évaluation pour",
    "assessments.deleteConfirmSuffix": " ? Cette action est irréversible.",
    "assessments.thisCompany": "cette entreprise",
    "assessments.selectNewPlanToContinue":
      "Sélectionnez un nouveau forfait pour continuer :",
    "assessments.allRightsReserved": "Tous droits réservés",
    "assessments.companyNameRequired": "Le nom de l’entreprise est requis.",
    "assessments.missingAccountInformation":
      "Informations de compte manquantes.",
    "assessments.unableToValidateSubscription":
      "Impossible de valider l’abonnement.",
    "assessments.unableToStartCheckout":
      "Impossible de démarrer le paiement. Veuillez réessayer plus tard.",
    "assessments.failedToCreateCheckoutSession":
      "Échec de la création de la session de paiement.",
    "assessments.failedToSaveAssessment":
      "Échec de l’enregistrement de l’évaluation.",
    "assessments.notAuthorized": "Non autorisé.",
    "assessments.missingAssessmentId": "Identifiant d’évaluation manquant.",
    "assessments.failedToDeleteAssessment":
      "Échec de la suppression de l’évaluation.",
    "status.active": "En cours",
    "status.completed": "Terminée",
    "status.aiWorkflowCompleted": "Travail IA terminé",
    "status.aiPoliciesCompleted": "Politiques IA terminées",
    Untitled: "Sans titre",
    "dashboard.activeAssessments": "Évaluations actives",
    "dashboard.completed": "Évaluations terminées",
    "dashboard.total": "Évaluations totales",
    "dashboard.currentlyInProgress": "En cours",
    "dashboard.allTime": "Tout le temps",
    "dashboard.allAssessments": "Toutes les évaluations",
    "dashboard.checking": "Vérification...",
    "dashboard.newAssessment": "Nouvelle évaluation",
    "dashboard.createNewAssessment": "Créer une nouvelle évaluation",
    "dashboard.subscriptionRequired": "Abonnement requis",
    "dashboard.subscriptionInfo":
      "Vous avez besoin d’un abonnement actif avant de pouvoir créer une nouvelle évaluation.",
    "dashboard.selectPlan": "Sélectionnez un plan pour continuer :",
    "dashboard.startingCheckout": "Démarrage du paiement...",
    "dashboard.select": "Sélectionner",
    "dashboard.complete": "complété",
    "dashboard.interviewee": "Personne interviewée",
    "dashboard.missingFiles": "fichiers manquants",
    "dashboard.aimHigher": "Visez plus haut avec l’aide de l’IA",
    "dashboard.title": "Tableau de bord",
    "dashboard.activeAssessmentsLabel": "Évaluations actives",
    "dashboard.completedAssessmentsLabel": "Évaluations terminées",
    "dashboard.needsAttention": "Nécessite une attention",
    "dashboard.viewAll": "Tout voir",
    "dashboard.noAssessmentsFound": "Aucune évaluation trouvée.",
    "Processing…": "Traitement…",
    "Processing… This may take a few minutes":
      "Traitement… Cela peut prendre quelques minutes",
    "Overall Progress": "Progression globale",
    "completed with transcripts": "terminées avec transcriptions",
    "Document ready for download": "Document prêt à télécharger",
    "All interviews complete": "Tous les entretiens sont terminés",
    "Complete all interviews & upload transcripts first":
      "Terminez tous les entretiens et téléversez d’abord les transcriptions",
    "Ready to generate": "Prêt à générer",
    "At least 1 interview with transcript required":
      "Au moins 1 entretien avec transcription est requis",
    "interviewee(s)": "personne(s) interviewée(s)",
    "Delete Document": "Supprimer le document",
    "assessment.deleteDocumentConfirmPrefix":
      "Êtes-vous sûr de vouloir supprimer le document",
    "assessment.deleteDocumentConfirmSuffix":
      "? Cette action est irréversible.",
    "Edit Interviewee": "Modifier la personne interviewée",
    Yes: "Oui",
    No: "Non",
    Notice: "Avis",
    Notification: "Notification",
    "Action failed": "Échec de l’action",
    "Unable to complete the action.":
      "Impossible de terminer l’action.",
    "Record only your microphone, or include meeting/system audio by sharing a tab/window with system audio enabled.":
      "Enregistrez uniquement votre microphone, ou incluez l’audio de la réunion/du système en partageant un onglet ou une fenêtre avec l’audio système activé.",
    "Are you sure you want to proceed? This action cannot be undone.":
      "Êtes-vous sûr de vouloir continuer ? Cette action est irréversible.",
    "Generating document… please wait.":
      "Génération du document… veuillez patienter.",
    "Elapsed": "Écoulé",
    "Add Interviewees": "Ajouter des personnes interviewées",
    "Add Interviewee": "Ajouter une personne interviewée",
    "Missing fields": "Champs manquants",
    "Name and Position are required.":
      "Le nom et le poste sont requis.",
    "Invalid file": "Fichier non valide",
    "Please upload a CSV file.": "Veuillez téléverser un fichier CSV.",
    "Upload CSV": "Téléverser un CSV",
    "CSV format: Name, Role, Type, Email, Phone (one per row). First row can be a header.":
      "Format CSV : nom, rôle, type, e-mail, téléphone (une personne par ligne). La première ligne peut être un en-tête.",
    "Choose CSV file": "Choisir un fichier CSV",
    "interviewee(s) found": "personne(s) interviewée(s) trouvée(s)",
    "Submit Support Ticket": "Soumettre un ticket d’assistance",
    Company: "Entreprise",
    Plan: "Forfait",
    "Contact Name": "Nom du contact",
    Email: "E-mail",
    Phone: "Téléphone",
    "Category *": "Catégorie *",
    "Priority *": "Priorité *",
    "Subject *": "Objet *",
    Description: "Description",
    "Select category": "Sélectionner une catégorie",
    "Select priority": "Sélectionner une priorité",
    "Technical Issue": "Problème technique",
    "Billing / Account": "Facturation / Compte",
    "Feature Request": "Demande de fonctionnalité",
    "Assessment Help": "Aide à l’évaluation",
    Other: "Autre",
    Low: "Faible",
    Medium: "Moyenne",
    High: "Élevée",
    Urgent: "Urgente",
    "Brief summary of your issue": "Bref résumé de votre problème",
    "Provide details about your issue, including steps to reproduce if applicable...":
      "Décrivez votre problème, y compris les étapes pour le reproduire si applicable...",
    "Submit Ticket": "Soumettre le ticket",
    "Please fill in all required fields":
      "Veuillez remplir tous les champs obligatoires",
    "Failed to submit ticket.": "Échec de la soumission du ticket.",
    "Support ticket submitted": "Ticket d’assistance soumis",
    "support.ticketSubmittedPrefix": "Le ticket pour",
    "support.ticketSubmittedSuffix": "a été envoyé. Demande soumise.",
    "Submit failed": "Échec de la soumission",
    "Could not submit support ticket.":
      "Impossible de soumettre le ticket d’assistance.",
    "settings.subtitle":
      "Configurez la marque, la langue, l’abonnement et les préférences de facturation.",
    "Account Settings": "Paramètres du compte",
    "Update your contact information and password.":
      "Mettez à jour vos coordonnées et votre mot de passe.",
    "Full Name": "Nom complet",
    "Enter your full name": "Entrez votre nom complet",
    "Enter your phone number": "Entrez votre numéro de téléphone",
    "New Password": "Nouveau mot de passe",
    "Leave blank to keep current": "Laissez vide pour conserver l’actuel",
    "Confirm Password": "Confirmer le mot de passe",
    "Confirm new password": "Confirmez le nouveau mot de passe",
    "White Label": "Marque blanche",
    "Your company name and logo appear on all generated documents.":
      "Le nom et le logo de votre entreprise apparaissent sur tous les documents générés.",
    "Enter your company name": "Entrez le nom de votre entreprise",
    "Company Logo": "Logo de l’entreprise",
    "Click to upload logo": "Cliquez pour téléverser le logo",
    "PNG, JPG, SVG up to 2MB": "PNG, JPG, SVG jusqu’à 2 Mo",
    "Document Header Preview": "Aperçu de l’en-tête du document",
    "Your Company Name": "Nom de votre entreprise",
    Language: "Langue",
    "Select the language for generated output documents and reports.":
      "Sélectionnez la langue des documents et rapports générés.",
    "Output Language": "Langue de sortie",
    "This determines the language used in AI Readiness Assessments and Policy documents.":
      "Cela détermine la langue utilisée dans les évaluations de préparation IA et les documents de politiques.",
    Subscription: "Abonnement",
    "Billed quarterly at": "Facturé trimestriellement à",
    "Pause Subscription": "Suspendre l’abonnement",
    "Quarterly Invoices": "Factures trimestrielles",
    "Loading upcoming invoices...": "Chargement des prochaines factures...",
    Paid: "Payée",
    Upcoming: "À venir",
    "No upcoming invoices found.": "Aucune facture à venir trouvée.",
    "Upgrade to Pro": "Passer à Pro",
    "settings.upgradeCopy":
      "Vous êtes actif depuis plus de 6 mois et vous approchez de votre limite d’évaluations. Passez à Pro pour 3 évaluations par mois à 500 $ par évaluation.",
    "months remaining": "mois restants",
    "Upgrade to": "Passer à",
    "subscription.enterpriseUpgradeCopy":
      "Vous dépassez 3 évaluations par mois. Passez à Enterprise pour des évaluations illimitées et une assistance prioritaire.",
    Billing: "Facturation",
    "Manage your payment method and billing details.":
      "Gérez votre mode de paiement et vos informations de facturation.",
    "Billing Details": "Détails de facturation",
    "Next billing": "Prochaine facturation",
    Amount: "Montant",
    "Open Billing Portal": "Ouvrir le portail de facturation",
    "Saving...": "Enregistrement...",
    "Save Settings": "Enregistrer les paramètres",
    "Account Settings Updated": "Paramètres du compte mis à jour",
    "Your account details have been saved successfully.":
      "Les détails de votre compte ont été enregistrés avec succès.",
    Done: "Terminé",
    "Please contact west@gowest.ai to pause your account and payments.":
      "Veuillez contacter west@gowest.ai pour suspendre votre compte et vos paiements.",
    "Invoice load failed": "Échec du chargement des factures",
    "Unable to load invoices.": "Impossible de charger les factures.",
    "Could not load upcoming invoices.":
      "Impossible de charger les prochaines factures.",
    "Cloudinary widget failed to load.":
      "Échec du chargement du widget Cloudinary.",
    "Missing user id. Please sign in again.":
      "Identifiant utilisateur manquant. Veuillez vous reconnecter.",
    "Upload failed": "Échec du téléversement",
    "Could not upload logo.": "Impossible de téléverser le logo.",
    "Logo uploaded": "Logo téléversé",
    "Your company logo has been saved.":
      "Le logo de votre entreprise a été enregistré.",
    "Unable to open upload widget.":
      "Impossible d’ouvrir le widget de téléversement.",
    "Please upload an image file.": "Veuillez téléverser un fichier image.",
    "Settings saved": "Paramètres enregistrés",
    "Your preferences have been updated.":
      "Vos préférences ont été mises à jour.",
    "Full name is required.": "Le nom complet est requis.",
    "Full name required": "Nom complet requis",
    "Enter your full name before saving account settings.":
      "Entrez votre nom complet avant d’enregistrer les paramètres du compte.",
    "Phone number is required.": "Le numéro de téléphone est requis.",
    "Phone number required": "Numéro de téléphone requis",
    "Enter your phone number before saving account settings.":
      "Entrez votre numéro de téléphone avant d’enregistrer les paramètres du compte.",
    "Email is required.": "L’e-mail est requis.",
    "Email missing": "E-mail manquant",
    "Please sign in again before updating account settings.":
      "Veuillez vous reconnecter avant de mettre à jour les paramètres du compte.",
    "New password is required.": "Le nouveau mot de passe est requis.",
    "New password required": "Nouveau mot de passe requis",
    "Enter a new password before confirming it.":
      "Entrez un nouveau mot de passe avant de le confirmer.",
    "Passwords do not match.": "Les mots de passe ne correspondent pas.",
    "Passwords do not match": "Les mots de passe ne correspondent pas",
    "Confirm your new password before saving.":
      "Confirmez votre nouveau mot de passe avant d’enregistrer.",
    Saved: "Enregistré",
    "Account settings updated.": "Paramètres du compte mis à jour.",
    "Save failed": "Échec de l’enregistrement",
    "Failed to save account settings.":
      "Échec de l’enregistrement des paramètres du compte.",
    "Assessment limit unavailable": "Limite d’évaluations indisponible",
    "Quarterly invoice": "Facture trimestrielle",
    "1 assessment/month (3/quarter)": "1 évaluation/mois (3/trimestre)",
    "3 assessments/month": "3 évaluations/mois",
    "Unlimited assessments": "Évaluations illimitées",
    Custom: "Personnalisé",
    Close: "Fermer",
    Confirm: "Confirmer",
    "Are you sure?": "Êtes-vous sûr ?",
    "Nothing needs attention right now.":
      "Aucune action n’est requise pour l’instant.",
    "AI Workflow Completed": "Travail IA terminé",
    "AI Policies Completed": "Politiques IA terminées",
    "Completed Assessments": "Évaluations terminées",
    "No assessments yet": "Aucune évaluation pour l’instant",
  },
};

const getStoredLanguage = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getStoredLanguage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {}
  }, [language]);

  const t = useMemo(
    () =>
      (key, fallback = "") => {
        const source = String(key ?? "");
        if (language === "fr") {
          return (
            translations.fr[source] ||
            translations.en[source] ||
            fallback ||
            source
          );
        }
        return translations.en[source] || fallback || source;
      },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
